import React, { useState, useEffect, useRef } from 'react'
import { useToast } from './ToastProvider'
import analytics from '../utils/analytics'

const isRetryableStatus = (status) => Number(status) >= 500
const shouldTripCircuit = (error) => Boolean(error?.isNetworkError || isRetryableStatus(error?.status))
const shouldRetryRequest = (error) => Boolean(error?.isNetworkError || isRetryableStatus(error?.status))
const isAbortLikeError = (error) => {
  const name = String(error?.name || '').toLowerCase()
  const message = String(error?.message || '').toLowerCase()
  return (
    name === 'aborterror' ||
    message.includes('aborted') ||
    message.includes('aborterror') ||
    message.includes('the user aborted a request')
  )
}

export default function NetworkErrorHandler({ children }) {
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const [retryCount, setRetryCount] = useState(0)
  const [lastError, setLastError] = useState(null)
  const { addToast } = useToast()
  const pendingRequestsRef = useRef(new Map())
  const circuitRef = useRef({
    open: false,
    failures: 0,
    lastFailure: 0,
    openedAt: 0
  })
  const CIRCUIT_BREAKER_THRESHOLD = 5
  const CIRCUIT_BREAKER_TIMEOUT_MS = 10000
  const REQUEST_BACKOFF_BASE = 500

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true)
      if (retryCount > 0) {
        addToast({
          type: 'success',
          title: 'Connection Restored',
          message: 'You\'re back online. Your changes will be synced automatically.'
        })
        setRetryCount(0)
      }
    }

    const handleOffline = () => {
      setIsOnline(false)
      addToast({
        type: 'warning',
        title: 'Connection Lost',
        message: 'You\'re offline. Changes will be saved locally and synced when you reconnect.'
      })
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [retryCount, addToast])

  // Global error handler for fetch requests
  useEffect(() => {
    const originalFetch = window.fetch

    window.fetch = async (...args) => {
      const requestInfo = args[0]
      const requestInit = args[1] || {}
      const url = typeof requestInfo === 'string' ? requestInfo : requestInfo?.url || ''
      const method = (typeof requestInfo === 'string' ? requestInit.method : requestInfo?.method) || requestInit.method || 'GET'
      const requestKey = `${method}:${url}`
      const isSupabaseRequest = (() => {
        try {
          const parsedUrl = new URL(url, window.location.origin)
          const host = parsedUrl.hostname.toLowerCase()
          return (
            host.includes('supabase.') ||
            host.endsWith('supabase.co') ||
            host.endsWith('supabase.net') ||
            host.endsWith('supabase.in')
          )
        } catch {
          return url.includes('supabase')
        }
      })()

      if (isSupabaseRequest) {
        return originalFetch(...args)
      }

      const circuit = circuitRef.current
      const now = Date.now()
      if (circuit.open && now - circuit.openedAt < CIRCUIT_BREAKER_TIMEOUT_MS) {
        const circuitError = new Error('Network circuit breaker open')
        circuitError.isNetworkError = true
        circuitError.isCircuitOpen = true
        handleNetworkError(circuitError, requestInfo)
        throw circuitError
      }

      if (pendingRequestsRef.current.has(requestKey)) {
        // Give every caller its own stream to avoid "body stream already read" errors.
        return pendingRequestsRef.current
          .get(requestKey)
          .then((response) => response.clone())
      }

      const performFetch = async (attempt = 0) => {
        try {
          const response = await originalFetch(...args)

          if (!response.ok) {
            if (isRetryableStatus(response.status) && attempt < 2) {
              await new Promise(resolve => setTimeout(resolve, REQUEST_BACKOFF_BASE * Math.pow(2, attempt)))
              return performFetch(attempt + 1)
            }

            const error = new Error(`HTTP ${response.status}: ${response.statusText}`)
            error.status = response.status
            error.response = response
            throw error
          }

          circuit.failures = 0
          circuit.open = false
          circuit.lastFailure = 0
          return response
        } catch (error) {
          if (isAbortLikeError(error)) {
            throw error
          }

          const handledError = (error.name === 'TypeError' && error.message.includes('fetch'))
            ? Object.assign(new Error('Network connection failed'), { isNetworkError: true })
            : error

          if (shouldTripCircuit(handledError)) {
            circuit.failures += 1
            circuit.lastFailure = Date.now()

            if (circuit.failures >= CIRCUIT_BREAKER_THRESHOLD) {
              circuit.open = true
              circuit.openedAt = Date.now()
              console.warn('[Network] Circuit breaker opened due to repeated server/network failures')
            }
          }

          if (!handledError.isCircuitOpen) {
            handleNetworkError(handledError, requestInfo)
          }

          if (attempt < 2 && shouldRetryRequest(handledError)) {
            await new Promise(resolve => setTimeout(resolve, REQUEST_BACKOFF_BASE * Math.pow(2, attempt)))
            return performFetch(attempt + 1)
          }

          throw handledError
        }
      }

      const pendingPromise = performFetch()
      pendingRequestsRef.current.set(requestKey, pendingPromise)

      try {
        const response = await pendingPromise
        // Clone before returning so each consumer receives an unread body
        return response.clone()
      } finally {
        pendingRequestsRef.current.delete(requestKey)
      }
    }

    return () => {
      window.fetch = originalFetch
    }
  }, [])

  const handleNetworkError = (error, requestInfo) => {
    if (isAbortLikeError(error)) {
      return
    }

    const url = typeof requestInfo === 'string' ? requestInfo : requestInfo?.url || 'unknown'
    
    setLastError({
      error,
      url,
      timestamp: new Date().toISOString()
    })
    
    setRetryCount(prev => prev + 1)

    // Track error for analytics
    analytics.trackError('network_error', error.message, null, {
      url,
      status: error.status,
      isNetworkError: error.isNetworkError,
      retryCount: retryCount + 1
    })

    // Show appropriate error message
    if (error.isNetworkError) {
      addToast({
        type: 'error',
        title: 'Network Error',
        message: 'Unable to connect to the server. Please check your internet connection.',
        dedupeKey: 'network-error'
      })
    } else if (error.status === 401) {
      addToast({
        type: 'error',
        title: 'Authentication Required',
        message: 'Please sign in to continue.',
        dedupeKey: 'auth-required'
      })
    } else if (error.status === 403) {
      addToast({
        type: 'error',
        title: 'Access Denied',
        message: 'You don\'t have permission to perform this action.',
        dedupeKey: 'access-denied'
      })
    } else if (error.status === 404) {
      addToast({
        type: 'error',
        title: 'Not Found',
        message: 'The requested resource was not found.',
        dedupeKey: 'resource-not-found'
      })
    } else if (error.status === 429) {
      addToast({
        type: 'warning',
        title: 'Too Many Requests',
        message: 'The site is receiving too many requests at once. Please wait a moment and try again.',
        dedupeKey: 'rate-limit'
      })
    } else if (error.status >= 500) {
      addToast({
        type: 'error',
        title: 'Server Error',
        message: 'Something went wrong on our end. Please try again later.',
        dedupeKey: 'server-error'
      })
    } else {
      addToast({
        type: 'error',
        title: 'Request Failed',
        message: 'Unable to complete your request. Please try again.',
        dedupeKey: `request-failed-${error.status || 'unknown'}`
      })
    }
  }

  // Retry mechanism for failed requests
  const retryRequest = async (requestFn, maxRetries = 3) => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await requestFn()
      } catch (error) {
        if (attempt === maxRetries) {
          throw error
        }
        
        // Wait before retrying (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000))
        
        addToast({
          type: 'info',
          title: 'Retrying...',
          message: `Attempt ${attempt + 1} of ${maxRetries}`
        })
      }
    }
  }

  // Expose retry function globally
  useEffect(() => {
    window.retryRequest = retryRequest
    return () => {
      delete window.retryRequest
    }
  }, [])

  // Show network status indicator
  if (!isOnline) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900 p-4">
        <div className="max-w-md mx-auto text-center p-8 bg-gray-800 rounded-lg border border-gray-700">
          <div className="mx-auto mb-4 h-14 w-14 rounded-full border-2 border-yellow-500 bg-yellow-500/10" aria-hidden="true" />
          <h1 className="text-2xl font-bold text-gray-100 mb-4">You're Offline</h1>
          <p className="text-gray-400 mb-6">
            Please check your internet connection and try again.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="bg-yellow-500 text-gray-900 px-6 py-3 rounded-lg font-semibold hover:bg-yellow-400 transition-colors"
          >
            Retry Connection
          </button>
        </div>
      </div>
    )
  }

  return children
}

// Hook for handling network errors in components
export function useNetworkError() {
  const [error, setError] = useState(null)
  const [isRetrying, setIsRetrying] = useState(false)

  const handleError = (error, context = '') => {
    setError({ error, context, timestamp: new Date().toISOString() })
    
    analytics.trackError('component_network_error', error.message, null, {
      context,
      status: error.status,
      stack: error.stack
    })
  }

  const retry = async (requestFn, maxRetries = 3) => {
    setIsRetrying(true)
    setError(null)
    
    try {
      const result = await window.retryRequest(requestFn, maxRetries)
      setIsRetrying(false)
      return result
    } catch (error) {
      setIsRetrying(false)
      handleError(error, 'retry_failed')
      throw error
    }
  }

  const clearError = () => {
    setError(null)
  }

  return {
    error,
    isRetrying,
    handleError,
    retry,
    clearError
  }
}
